import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';

import * as moment from 'moment';

import { DateRangePickerOptionsInterface } from '../@models/date-range-picker.options';
import { DateRangePickerInputEvent } from '../@models/date-range-picker-range.model';
import { SideEnum } from '../@models/date-range-picker-calendar-side.enum';

@Injectable()
export class MwDateRangePickerService {
  private options: DateRangePickerOptionsInterface;

  constructor(
    private translateService: TranslateService,
    private datePipe: DatePipe
  ) {}

  setup(opts: DateRangePickerOptionsInterface): void {
    this.options = opts;
  }

  getDuration(start: moment.Moment, end: moment.Moment): number {
    if (start != null && end != null && end >= start) {
      return end.diff(start, 'days') + 1;
    }

    return 0;
  }

  getRangeText(
    label: string,
    start: Date,
    end: Date,
    isShowAsDate: boolean
  ): string {
    if (
      !isShowAsDate &&
      !!label &&
      label !== this.options.locale.customRangeLabel
    ) {
      return this.translateService.instant(label);
    }

    return this.getDatesAsText(moment(start), moment(end), isShowAsDate);
  }

  getChoosenRange(start: moment.Moment, end: moment.Moment): string {
    const ranges = this.options.ranges;

    if (ranges && ranges.length > 0) {
      for (const range of ranges) {
        // ignore times when comparing dates if time picker is not enabled
        if (
          start &&
          end &&
          start.format('YYYY-MM-DD') ===
            moment(range.start).format('YYYY-MM-DD') &&
          end.format('YYYY-MM-DD') === moment(range.end).format('YYYY-MM-DD')
        ) {
          return range.label;
        }
      }
    }

    return this.options.locale.customRangeLabel;
  }

  getNormalizedStart(start: any): moment.Moment {
    if (typeof start === 'string') {
      start = moment(start, this.options.locale.format);
    }

    if (typeof start === 'object') {
      start = moment(start);
    }
    start = start.startOf('day');

    if (this.options.minDate && start.isBefore(moment(this.options.minDate))) {
      start = moment(this.options.minDate);
    }

    if (this.options.maxDate && start.isAfter(moment(this.options.maxDate))) {
      start = moment(this.options.maxDate);
    }

    return start;
  }

  getNormalizedEnd(end: any, start: moment.Moment): moment.Moment {
    if (typeof end === 'string') {
      end = moment(end, this.options.locale.format);
    }

    if (typeof end === 'object') {
      end = moment(end);
    }

    if (end.isBefore(start)) {
      end = start.clone();
    }

    if (this.options.maxDate && end.isAfter(moment(this.options.maxDate))) {
      end = moment(this.options.maxDate);
    }

    if (
      this.options.dateLimit &&
      start.clone().add(this.options.dateLimit).isBefore(end)
    ) {
      end = start.clone().add(this.options.dateLimit);
    }

    end.set('hours', 23).set('minutes', 59).set('seconds', 59);

    return end;
  }

  isDisabledRange(range: DateRangePickerInputEvent): boolean {
    if (range.label === this.options.locale.customRangeLabel) {
      return false;
    }
    const rangeMarkers = [range.start, range.end];
    const areBothBefore = rangeMarkers.every((date) => {
      if (!this.options.minDate) {
        return false;
      }
      return +date < +this.options.minDate;
    });

    const areBothAfter = rangeMarkers.every((date) => {
      if (!this.options.maxDate) {
        return false;
      }
      return +date > +this.options.maxDate;
    });
    return areBothBefore || areBothAfter;
  }

  monthOrYearChanged(
    start,
    leftCalendar,
    rightCalendar,
    calendarVariables: { left: any; right: any },
    month: number,
    year: any,
    side: SideEnum
  ): void {
    const isLeft = side === SideEnum.left;

    if (!isLeft) {
      if (
        year < start.year() ||
        (year === start.year() && month < start.month())
      ) {
        month = start.month();
        year = start.year();
      }
    }

    if (this.options.minDate) {
      if (
        year < this.options.minDate.getFullYear() ||
        (year === this.options.minDate.getFullYear() &&
          month < this.options.minDate.getMonth())
      ) {
        month = this.options.minDate.getMonth();
        year = this.options.minDate.getFullYear();
      }
    }

    if (this.options.maxDate) {
      if (
        year > this.options.maxDate.getFullYear() ||
        (year === this.options.maxDate.getFullYear() &&
          month > this.options.maxDate.getMonth())
      ) {
        month = this.options.maxDate.getMonth();
        year = this.options.maxDate.getFullYear();
      }
    }
    calendarVariables[side].dropdowns.currentYear = year;
    calendarVariables[side].dropdowns.currentMonth = month;
    if (isLeft) {
      leftCalendar.month.month(month).year(year);
      if (this.options.linkedCalendars) {
        rightCalendar.month = leftCalendar.month.clone().add(1, 'month');
      }
    } else {
      rightCalendar.month.month(month).year(year);
      if (this.options.linkedCalendars) {
        leftCalendar.month = rightCalendar.month.clone().subtract(1, 'month');
      }
    }
  }

  buildRanges(): DateRangePickerInputEvent[] {
    const ranges = [];
    let start: moment.Moment;
    let end: moment.Moment;
    if (this.options.ranges && typeof ranges === 'object') {
      for (const range of this.options.ranges) {
        if (typeof range.start === 'string') {
          start = moment(range.start, this.options.locale.format);
        } else {
          start = moment(range.start);
        }

        if (typeof range.end === 'string') {
          end = moment(range.end, this.options.locale.format);
        } else {
          end = moment(range.end);
        }

        // If the start or end date exceed those allowed by the minDate or maxSpan
        // options, shorten the range to the allowable period.
        if (
          this.options.minDate &&
          start.isBefore(moment(this.options.minDate))
        ) {
          range.start = this.options.minDate;
        }

        let maxDate = moment(this.options.maxDate);
        if (
          this.options.maxSpan &&
          maxDate &&
          start.clone().add(this.options.maxSpan).isAfter(maxDate)
        ) {
          maxDate = start.clone().add(this.options.maxSpan);
        }
        if (maxDate && end.isAfter(maxDate)) {
          range.end = maxDate.toDate();
        }

        // If the end of the range is before the minimum or the start of the range is
        // after the maximum, don't display this range option at all.
        if (
          (this.options.minDate &&
            end.isBefore(moment(this.options.minDate), 'day')) ||
          (maxDate && start.isAfter(maxDate, 'day'))
        ) {
          continue;
        }
        ranges.push(range);
      }

      const customRange = {
        label: this.options.locale.customRangeLabel,
        start: null,
        end: null,
      };
      ranges.push(customRange);
    }
    return ranges;
  }

  updateMonthsInView(start, end, rightCalendar: any, leftCalendar: any): void {
    if (end) {
      // if both dates are visible already, do nothing
      if (
        leftCalendar.month &&
        rightCalendar.month &&
        ((start &&
          leftCalendar &&
          start.format('YYYY-MM') === leftCalendar.month.format('YYYY-MM')) ||
          (start &&
            rightCalendar &&
            start.format('YYYY-MM') ===
              rightCalendar.month.format('YYYY-MM'))) &&
        (end.format('YYYY-MM') === leftCalendar.month.format('YYYY-MM') ||
          end.format('YYYY-MM') === rightCalendar.month.format('YYYY-MM'))
      ) {
        return;
      }
      if (start) {
        if (!this.options.showCurrentDateOnRight) {
          leftCalendar.month = start.clone().date(2);
          if (
            !this.options.linkedCalendars &&
            (end.month() !== start.month() || end.year() !== start.year())
          ) {
            rightCalendar.month = end.clone().date(2);
          } else {
            rightCalendar.month = start.clone().date(2).add(1, 'month');
          }
        } else {
          rightCalendar.month = start.clone().date(2);
          if (
            !this.options.linkedCalendars &&
            (end.month() !== start.month() || end.year() !== start.year())
          ) {
            leftCalendar.month = end.clone().date(2);
          } else {
            leftCalendar.month = start.clone().date(2).add(-1, 'month');
          }
        }
      }
    } else {
      const startDate = start || moment();

      if (
        (!leftCalendar.month && !rightCalendar.month) ||
        (leftCalendar.month.format('YYYY-MM') !== startDate.format('YYYY-MM') &&
          rightCalendar.month.format('YYYY-MM') !== startDate.format('YYYY-MM'))
      ) {
        if (!this.options.showCurrentDateOnRight) {
          leftCalendar.month = startDate.clone().date(2);
          rightCalendar.month = startDate.clone().date(2).add(1, 'month');
        } else {
          leftCalendar.month = startDate.clone().date(2).add(-1, 'month');
          rightCalendar.month = startDate.clone().date(2);
        }
      }
    }
    if (
      this.options.maxDate &&
      this.options.linkedCalendars &&
      rightCalendar.month > this.options.maxDate
    ) {
      rightCalendar.month = moment(this.options.maxDate).date(2);
      leftCalendar.month = moment(this.options.maxDate)
        .date(2)
        .subtract(1, 'month');
    }
  }

  buildCalendar(
    start: moment.Moment,
    end: moment.Moment,
    endHover: moment.Moment,
    calendarVariables,
    side: SideEnum,
    leftCalendar,
    rightCalendar
  ): void {
    // side enum
    const mainCalendar: any =
      side === SideEnum.left ? leftCalendar : rightCalendar;
    const month = mainCalendar.month.month();
    const year = mainCalendar.month.year();
    const hour = mainCalendar.month.hour();
    const minute = mainCalendar.month.minute();
    const second = mainCalendar.month.second();
    const daysInMonth = moment([year, month]).daysInMonth();
    const firstDay = moment([year, month, 1]);
    const lastDay = moment([year, month, daysInMonth]).endOf('day');
    const lastMonth = moment(firstDay).subtract(1, 'month').month();
    const lastYear = moment(firstDay).subtract(1, 'month').year();
    const daysInLastMonth = moment([lastYear, lastMonth]).daysInMonth();
    const dayOfWeek = firstDay.day();
    // initialize a 6 rows x 7 columns array for the calendar
    const calendar: any = [];
    calendar.firstDay = firstDay;
    calendar.lastDay = lastDay;

    for (let i = 0; i < 6; i++) {
      calendar[i] = [];
    }

    // populate the calendar with date objects
    let startDay =
      daysInLastMonth - dayOfWeek + this.options.locale.firstDay + 1;
    if (startDay > daysInLastMonth) {
      startDay -= 7;
    }
    if (dayOfWeek === this.options.locale.firstDay) {
      startDay = daysInLastMonth - 6;
    }

    let curDate = moment([lastYear, lastMonth, startDay, 12, minute, second]);

    for (
      let i = 0, col = 0, row = 0;
      i < 42;
      i++, col++, curDate = moment(curDate).add(24, 'hour')
    ) {
      if (i > 0 && col % 7 === 0) {
        col = 0;
        row++;
      }

      if (curDate.isBefore(firstDay) || curDate.isAfter(lastDay)) {
        calendar[row][col] = null;
      } else {
        calendar[row][col] = curDate
          .clone()
          .hour(hour)
          .minute(minute)
          .second(second);
      }

      let dayPointer = calendar[row][col];
      curDate.hour(12);

      if (
        this.options.minDate &&
        dayPointer &&
        dayPointer.format('YYYY-MM-DD') ===
          moment(this.options.minDate).format('YYYY-MM-DD') &&
        dayPointer.isBefore(moment(this.options.minDate)) &&
        side === 'left'
      ) {
        dayPointer = moment(this.options.minDate);
      }

      if (
        this.options.maxDate &&
        dayPointer &&
        dayPointer.format('YYYY-MM-DD') ===
          moment(this.options.maxDate).format('YYYY-MM-DD') &&
        dayPointer.isAfter(moment(this.options.maxDate)) &&
        side === 'right'
      ) {
        dayPointer = moment(this.options.maxDate);
      }
    }

    // make the calendar object available to hoverDate/clickDate
    if (side === SideEnum.left) {
      leftCalendar.calendar = calendar;
    } else {
      rightCalendar.calendar = calendar;
    }
    //
    // Display the calendar
    //
    const minDate =
      side === 'left' ? this.options.minDate : start ? start.toDate() : null;
    let maxDate = this.options.maxDate;
    // adjust maxDate to reflect the dateLimit setting in order to
    // grey out end dates beyond the dateLimit
    if (end === null && this.options.dateLimit) {
      const maxLimit = start.clone().add(this.options.dateLimit).endOf('day');
      if (!maxDate || maxLimit.isBefore(moment(maxDate))) {
        maxDate = maxLimit.toDate();
      }
    }
    calendarVariables[side] = {
      month,
      year,
      hour,
      minute,
      second,
      daysInMonth,
      firstDay,
      lastDay,
      lastMonth,
      lastYear,
      daysInLastMonth,
      dayOfWeek,
      // other vars
      calRows: Array.from(Array(6).keys()),
      calCols: Array.from(Array(7).keys()),
      classes: {},
      minDate,
      maxDate,
      calendar,
    };
    if (this.options.showDropdowns) {
      const currentMonth = calendar[1][1].month();
      const currentYear = calendar[1][1].year();
      const maxYear = (maxDate && maxDate.getFullYear()) || currentYear + 5;
      const minYear = (minDate && minDate.getFullYear()) || currentYear - 50;
      const inMinYear = currentYear === minYear;
      const inMaxYear = currentYear === maxYear;
      const years = [];
      for (let y = minYear; y <= maxYear; y++) {
        years.push(y);
      }
      calendarVariables[side].dropdowns = {
        currentMonth,
        currentYear,
        maxYear,
        minYear,
        inMinYear,
        inMaxYear,
        monthArrays: Array.from(Array(12).keys()),
        yearArrays: years,
      };
    }

    this.buildCells(start, end, endHover, calendarVariables, calendar, side);
  }

  private buildCells(
    start: moment.Moment,
    end: moment.Moment,
    endHover: moment.Moment,
    calendarVariables,
    calendar,
    side: SideEnum
  ): void {
    for (let row = 0; row < 6; row++) {
      calendarVariables[side].classes[row] = {};
      const rowClasses = [];
      if (
        this.options.emptyWeekRowClass &&
        !this.hasCurrentMonthDays(calendarVariables[side].month, calendar[row])
      ) {
        rowClasses.push(this.options.emptyWeekRowClass);
      }
      for (let col = 0; col < 7; col++) {
        const classes = [];
        const dayPointer = calendar[row][col];

        if (!!dayPointer) {
          // highlight today's date
          if (dayPointer.isSame(new Date(), 'day')) {
            classes.push('today');
          }
          // highlight weekends
          if (dayPointer.isoWeekday() > 5) {
            classes.push('weekend');
          }
          // grey out the dates in other months displayed at beginning and end of this calendar
          if (dayPointer.month() !== calendar[1][1].month()) {
            classes.push('off');

            // mark the last day of the previous month in this calendar
            if (
              this.options.lastDayOfPreviousMonthClass &&
              (dayPointer.month() < calendar[1][1].month() ||
                calendar[1][1].month() === 0) &&
              dayPointer.date() === calendarVariables[side].daysInLastMonth
            ) {
              classes.push(this.options.lastDayOfPreviousMonthClass);
            }

            // mark the first day of the next month in this calendar
            if (
              this.options.firstDayOfNextMonthClass &&
              (dayPointer.month() > calendar[1][1].month() ||
                dayPointer.month() === 0) &&
              dayPointer.date() === 1
            ) {
              classes.push(this.options.firstDayOfNextMonthClass);
            }
          }
          // mark the first day of the current month with a custom class
          if (
            dayPointer.month() === calendar[1][1].month() &&
            dayPointer.date() === calendar.firstDay.date()
          ) {
            classes.push(this.options.firstMonthDayClass || '');
            classes.push('first');
          }
          // mark the last day of the current month with a custom class
          if (
            dayPointer.month() === calendar[1][1].month() &&
            dayPointer.date() === calendar.lastDay.date()
          ) {
            classes.push(this.options.lastMonthDayClass || '');
            classes.push('last');
          }
          // don't allow selection of dates before the minimum date
          if (
            this.options.minDate &&
            dayPointer.isBefore(moment(this.options.minDate), 'day')
          ) {
            classes.push('off', 'disabled');
          }
          // don't allow selection of dates after the maximum date
          if (
            calendarVariables[side].maxDate &&
            dayPointer.isAfter(moment(calendarVariables[side].maxDate), 'day')
          ) {
            classes.push('off', 'disabled');
          }
          // highlight the currently selected start date
          if (
            start &&
            dayPointer.format('YYYY-MM-DD') === start.format('YYYY-MM-DD')
          ) {
            classes.push('active', 'start-date');
          }
          // highlight the currently selected end date
          if (
            end != null &&
            dayPointer.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')
          ) {
            classes.push('active', 'end-date');
          }
          // highlight dates in-between the selected dates
          if (end != null && dayPointer >= start && dayPointer <= end) {
            classes.push('in-range');

            if (
              col === 0 &&
              (this.options.showWeekNumbers || this.options.showISOWeekNumbers)
            ) {
              classes.push('first');
            }
          }
          // Hover case
          if (
            end === null &&
            endHover != null &&
            dayPointer.format('YYYY-MM-DD') === endHover.format('YYYY-MM-DD')
          ) {
            classes.push('active', 'end-hover-date');
          }
          // highlight dates in-between the selected dates
          if (
            end === null &&
            endHover != null &&
            dayPointer >= start &&
            dayPointer <= endHover
          ) {
            classes.push('in-hover-range');

            if (
              col === 0 &&
              (this.options.showWeekNumbers || this.options.showISOWeekNumbers)
            ) {
              classes.push('first');
            }
          }
        } else {
          classes.push('off');
        }

        // store classes var
        let cname = '';
        let disabled = false;
        for (const className of classes) {
          cname += className + ' ';
          if (className === 'disabled') {
            disabled = true;
          }
        }
        if (!disabled) {
          cname += 'available';
        }
        calendarVariables[side].classes[row][col] = cname.replace(
          /^\s+|\s+$/g,
          ''
        );
      }
      calendarVariables[side].classes[row].classList = rowClasses.join(' ');
    }
  }

  /**
   * Find out if the current calendar row has current month days
   * (as opposed to consisting of only previous/next month days)
   */
  private hasCurrentMonthDays(currentMonth, row): boolean {
    for (let day = 0; day < 7; day++) {
      if (row[day].month() === currentMonth) {
        return true;
      }
    }
    return false;
  }

  private getDatesAsText(
    start: moment.Moment,
    end: moment.Moment,
    isShowAsDate: boolean
  ): string {
    let text = '';

    if (!start) {
      text = null;
    } else {
      if (isShowAsDate) {
        const startFormatted = this.datePipe.transform(
          start.toDate(),
          this.options.dateFormat
        );
        const endFormatted = this.datePipe.transform(
          end.toDate(),
          this.options.dateFormat
        );
        return startFormatted === endFormatted
          ? startFormatted
          : `${startFormatted} ${this.options.locale.separator} ${endFormatted}`;
      }

      const now = moment();

      if (now.isSame(start, 'date') && now.isSame(end, 'date')) {
        text = this.translateService.instant(
          'CORE.COMPONENT.CALENDAR.TOOLTIP.today'
        );
      } else {
        let separator = this.options.locale.separator;
        let startFormat = this.options.dateFormat;
        let endFormat = this.options.dateFormat;
        const yearsSame = start.isSame(end, 'year');
        const monthsSame = start.isSame(end, 'month');
        const daySame = start.isSame(end, 'day');

        if (now.isSame(start, 'year') && yearsSame) {
          endFormat = endFormat.replace('yyyy', '');
        }

        if (yearsSame) {
          startFormat = startFormat.replace('yyyy', '');
        }

        if (yearsSame && monthsSame) {
          startFormat = startFormat.replace('LLL', '');
          startFormat = startFormat.replace('LL', '');
          separator = separator.trim();
        }

        text =
          (daySame
            ? ''
            : this.datePipe.transform(start.toDate(), startFormat.trim()) +
              separator) +
          this.datePipe.transform(end.toDate(), endFormat.trim());
      }
    }

    return text;
  }
}
